import type { StateCreator } from 'zustand';
import { type RecordingStopPurpose } from '../ports';
import { type SessionRecordingStore } from '../types';
import { logger } from './lib/logger';

export type TaskFn = () => Promise<void> | void;

/** Serialized recording lifecycle operations. */
export type RecordingTaskType =
  | 'START_RECORDING'
  | 'STOP_RECORDING'
  | 'CLEANUP_RECORDINGS';

export interface Task {
  type: RecordingTaskType;
  promise: TaskFn;
  resolve: () => void;
  reject: (error: unknown) => void;
}

export interface TaskQueueSlice {
  processingTask: RecordingTaskType | null;
  taskQueue: Task[];
  isPaused: boolean;
  enqueueTask: (type: RecordingTaskType, promise: TaskFn) => Promise<void>;
  processTask: (singleTask?: boolean) => Promise<void>;
  toggleIsPaused: () => void;

  // Task wrappers.
  startTask: (isTabSharingSession: boolean) => Promise<string>;
  stopTask: (purpose?: RecordingStopPurpose) => Promise<void>;
  cleanupRecordingsTask: () => Promise<void>;
}

// Deduplicate matching queued and in-flight lifecycle operations. Checking
// `processingTask` as well as the queue closes the race window a task leaves
// open while it is mid-flight.
const DEDUP_LIST: RecordingTaskType[] = [
  'START_RECORDING',
  'STOP_RECORDING',
  'CLEANUP_RECORDINGS',
];

/**
 * How long a task may hold the processing slot before the queue reports itself
 * as wedged.
 *
 * Two minutes, which is far beyond any lifecycle operation this queue runs — a
 * start, a stop or a cleanup that has not settled in two minutes is not slow,
 * it is stuck — and far enough beyond that a genuinely slow one on a bad
 * network never files a report. The number only has to be past plausible; being
 * too generous costs a later log line, being too tight costs the line its
 * credibility.
 */
export const TASK_QUEUE_WEDGE_ALERT_MS = 120_000;

export const createTaskQueueSlice: StateCreator<
  SessionRecordingStore,
  [],
  [],
  TaskQueueSlice
> = (set, get) => ({
  processingTask: null,
  taskQueue: [],
  isPaused: false,

  enqueueTask: async (type: RecordingTaskType, promise: TaskFn) => {
    // Duplicate dedup-list tasks are dropped unconditionally — a duplicate
    // START/STOP/CLEANUP enqueue is never correct. Telemetry sits inside the
    // guard so every dedup hit stays observable in Datadog.
    if (DEDUP_LIST.includes(type)) {
      const isQueued = get().taskQueue.some((t) => t.type === type);
      const isProcessing = get().processingTask === type;
      if (isQueued || isProcessing) {
        logger.warn({
          event_type: 'TASK_QUEUE_DEDUPLICATED',
          details: {
            type,
            queuedDuplicate: isQueued,
            processingDuplicate: isProcessing,
            currentQueueLength: get().taskQueue.length,
          },
        });
        return;
      }
    }

    return new Promise((resolve, reject) => {
      const operation: Task = { type, promise, resolve, reject };
      set((state) => ({ taskQueue: [...state.taskQueue, operation] }));
      void get().processTask();
    });
  },

  processTask: async (singleTask = false) => {
    const { processTask } = get();
    if (
      get().processingTask ||
      get().taskQueue.length === 0 ||
      (get().isPaused && !singleTask)
    )
      return;
    const [nextTask, ...remainingTasks] = get().taskQueue;
    set({ taskQueue: remainingTasks, processingTask: nextTask?.type });
    if (!nextTask) return;

    // A task that never settles pins this slot for the life of the process:
    // the `finally` below is the only thing that clears it, and it is reached
    // by way of `await nextTask.promise()`. Everything enqueued afterwards then
    // waits forever — and because dedupe compares task TYPE, a stop or a pause
    // behind a stuck start is ADMITTED rather than deduplicated, so not even
    // `TASK_QUEUE_DEDUPLICATED` fires. `STOP_TRIGGERED` is logged inside
    // `stop()`, which never runs. The result is every recording control dead, a
    // capture still running, and complete silence: the one failure mode here
    // that cannot be told apart from an idle queue.
    //
    // This does not un-wedge it, and deliberately so. Abandoning the task after
    // a timeout would resolve or reject a caller whose underlying operation is
    // still in flight — cutting a slow-but-real start, or letting a second one
    // begin over it — which for a clinical capture is worse than the wedge.
    // What it does is make the wedge SAY SO: once, past any plausible duration,
    // naming what holds the slot and what is stuck behind it.
    //
    // `heldForMs` is MEASURED, not the threshold echoed back. A field reading
    // the same constant the timer was armed with says only "the timer fired",
    // which a test cannot falsify either — both sides are the same symbol. The
    // elapsed value is the one a reader actually needs, because a wedge found
    // by a later sweep is worth more than one found at the threshold.
    const startedAt = Date.now();
    const wedgeAlert = setTimeout(() => {
      logger.error({
        event_type: 'TASK_QUEUE_WEDGED',
        details: {
          type: nextTask.type,
          heldForMs: Date.now() - startedAt,
          waitingTaskCount: get().taskQueue.length,
          waitingTaskTypes: get().taskQueue.map((t) => t.type),
        },
      });
    }, TASK_QUEUE_WEDGE_ALERT_MS);

    try {
      await nextTask.promise();
      nextTask.resolve();
    } catch (err) {
      nextTask.reject(err);
    } finally {
      // Disarmed by the task SETTLING, either way — a task that throws already
      // rejects its caller and is not a wedge.
      clearTimeout(wedgeAlert);
      set({ processingTask: null });
      if (!singleTask) void processTask();
    }
  },

  toggleIsPaused: () => {
    set({ isPaused: !get().isPaused });
    if (!get().isPaused) void get().processTask();
  },

  // Task wrappers.
  // Capture the purpose in this operation's closure. A queued compensation
  // must not change error handling for an ordinary stop already in flight.
  stopTask: (purpose = 'ordinary') => {
    const stop = get().stop;
    return get().enqueueTask('STOP_RECORDING', () => stop(purpose));
  },

  startTask: async (isTabSharingSession: boolean) => {
    let result: string | undefined;

    await get().enqueueTask('START_RECORDING', async () => {
      result = await get().start(isTabSharingSession);
    });

    return result!;
  },

  cleanupRecordingsTask: () => {
    return get().enqueueTask('CLEANUP_RECORDINGS', get().cleanupRecordings);
  },
});
